import { useFormik } from "formik";
import Select from "../../../components/select";
import * as Yup from "yup";
import Input from "../../../components/input";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import TextArea from "../../../components/textArea";
import { createNetworkSubnet, listNetworkSubnet, updateSubnet } from "../../../hooks/local/userReducer";
import { useSubnetList } from "../layout/resuableEffect";


const EditSubnet = () => {

  document.title = "Edit subnet - Cloudvantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedProject = useSelector((state) => state.user.selectedProject);
  const selectedNetwork = JSON.parse(sessionStorage.getItem("selectedNetwork"));
  const selectedSubnet = JSON.parse(sessionStorage.getItem("selectedSubnet"));

    const [subnet, setSubnet] = useState(null);
    const [isFetched, setIsFetched] = useState(false); 
  
  const handleGetNetworkSubnet = async () => {
    if (!selectedSubnet?.subnetId || !selectedNetwork?.networkId || !selectedProject?.id) {
      return;
    }

    const subnetId = selectedSubnet?.subnetId;
    const networkId = selectedNetwork.networkId;
    const projectId = selectedProject.id;

    const data = {
      subnetId,
      networkId
    }

    const { payload } = await dispatch(listNetworkSubnet({ projectId, data }));
    // console.log(payload?.subnet);
    setSubnet(payload?.subnet);
    setIsFetched(true);
  };

  // Call handleGetNetwork immediately if not fetched yet
  if (!isFetched && selectedSubnet && selectedNetwork && selectedProject) {
    handleGetNetworkSubnet();
  }


  const [selectedTab, setSelectedTab] = useState("Subnet");

  const ipVersionOptions = [
    { value: "IPv4", label: "IPv4" },
    { value: "IPv6", label: "IPv6" },
  ];

    
    const formatAllocationPools = (pools) => {
      if (!pools || !Array.isArray(pools)) return "";
      return pools.map((pool) => `${pool.start},${pool.end}`).join("\n");
    };
  
   
    const parseAllocationPools = (input) => {
      if (!input) return [];
      return input
        .split("\n")
        .filter((line) => line.trim() !== "")
        .map((line) => {
          const [start, end] = line.split(",").map((ip) => ip.trim());
          return { start, end };
        });
    };

  const editSubnet = useFormik({
    initialValues: {
      subnetName: subnet?.name || "",
      enableDhcp: subnet?.enable_dhcp || true,
      networkAddress: subnet?.cidr || "",
      ipVersion: `IPv${subnet?.ip_version}` || "IPv4",
      gatewayIp: subnet?.gateway_ip || "",
      disableGateway: false,
      allocationPools: subnet?.allocation_pools || [],
      dnsServers:  subnet?.dns_nameservers || [],
      subnetpoolId: subnet?.subnetpool_id || "",
    },

    enableReinitialize: true,
    validationSchema: Yup.object({
      subnetName: Yup.string().required("Subnet name cannot be empty"),
      networkAddress: Yup.string().required("Network address cannot be empty"),
      // gatewayIp: Yup.string().when("disableGateway", {
      //   is: (value) => value === false,
      //   then: (schema) =>
      //     schema.matches(
      //       /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
      //       "Invalid Gateway IP format (e.g., 192.168.0.254)"
      //     ),
      //   otherwise: (schema) => schema.notRequired(),
      // }),
    }),

    onSubmit: async (values, { resetForm }) => {
      const { subnetName, networkAddress, ipVersion, gatewayIp, disableGateway, allocationPools, dnsServers } = values;


      let subnetData = {
        name: subnetName,
        projectId: selectedProject.id,
        networkId: selectedNetwork?.networkId,
        subnetId: selectedSubnet?.subnetId,
        // gateway_ip: gatewayIp
      };

      const payload = await dispatch(updateSubnet(subnetData));
      if (payload) {
        navigate("/networks");
        resetForm();
      }
    },
  });

  const subnetTabs = [
    "Subnet",
    "Subnet Details",
  ]

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue">Edit Subnet {selectedSubnet?.subnetName}</p>

      <p className="dark:text-gray-500 text-gray-400 text-sm mb-6 leading-loose md:leading-normal">
        Edit a subnet associated with the network. Advanced configuration is available by clicking on the “Subnet Details” tab.
      </p>


      <div className="flex flex-col justify-start items-start w-full border-b dark:border-b-gray-500 border-b-gray-400">
            <div className="flex justify-start items-center w-full md:w-[50%]">
                {subnetTabs.map((tab, i) => (
                    <button
                        key={i}
                        className={`w-full p-4 ${selectedTab === tab ? "bg-primaryBlue text-white rounded-md" : "text-primaryBlue"}  text-sm`}
                        onClick={() => setSelectedTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>
      </div>  


      <div className="grid gap-4 mt-10">
        <form onSubmit={editSubnet.handleSubmit}>

          {selectedTab === "Subnet" && (
            <div className="w-full lg:w-[50%] grid grid-cols-1 items-start gap-4">
                <div>
                <Input
                    type={"text"}
                    label="Subnet Name *"
                    name={"subnetName"}
                    onBlur={editSubnet.handleBlur}
                    onChange={editSubnet.handleChange}
                    placeholder={""}
                    value={editSubnet.values.subnetName}
                    onError={
                    editSubnet.errors.subnetName && editSubnet.touched.subnetName
                        ? editSubnet.errors.subnetName
                        : null
                    }
                />
                </div>

                <div>
                <Input
                    type={"text"}
                    label="Network Address *"
                    name={"networkAddress"}
                    onBlur={editSubnet.handleBlur}
                    onChange={editSubnet.handleChange}
                    placeholder={"Network address in CIDR format (e.g. 192.168.0.0/24, 2001:DB8::/48)"}
                    isLabelIcon={true}
                    disabled={true}
                    iconTitle="Network address in CIDR format (e.g. 192.168.0.0/24, 2001:DB8::/48)"
                    value={editSubnet.values.networkAddress}
                    onError={
                    editSubnet.errors.networkAddress && editSubnet.touched.networkAddress
                        ? editSubnet.errors.networkAddress
                        : null
                    }
                />
                </div>

                <div>
                <Select
                    name="ipVersion"
                    label="IP Version"
                    options={ipVersionOptions}
                    value={editSubnet.values.ipVersion}
                    onBlur={editSubnet.handleBlur}
                    disabled={true}
                    onChange={(event) =>
                        editSubnet.setFieldValue("ipVersion", event.target.value)
                    }
                    onError={
                    editSubnet.touched.ipVersion && editSubnet.errors.ipVersion
                        ? editSubnet.errors.ipVersion
                        : null
                    }
                />
                </div>

                {/* <div>
                <Input
                    type={"text"}
                    label="Gateway IP"
                    name={"gatewayIp"}
                    onBlur={editSubnet.handleBlur}
                    onChange={editSubnet.handleChange}
                    placeholder={"IP address of Gateway (e.g. 192.168.0.254)"}
                    isLabelIcon={true}
                    iconTitle="IP address of Gateway (e.g. 192.168.0.254) The default value is the first IP of the network address (e.g. 192.168.0.1 for 192.168.0.0/24). If you use the default, leave blank."
                    value={editSubnet.values.gatewayIp}
                    onError={
                    editSubnet.errors.gatewayIp && editSubnet.touched.gatewayIp
                        ? editSubnet.errors.gatewayIp
                        : null
                    }
                    disabled={editSubnet.values.disableGateway}
                />
                </div>

                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="disableGateway"
                        name="disableGateway"
                        checked={editSubnet.values.disableGateway}
                        onChange={(event) =>
                            editSubnet.setFieldValue("disableGateway", event.target.checked)
                        }
                    />
                    <label htmlFor="disableGateway" className="text-sm text-gray-700 dark:text-gray-300">
                        Disable Gateway
                    </label>
                </div> */}
            </div>
          )}

          {selectedTab === "Subnet Details" && (
            <div className="w-full">
                <div className="w-full lg:w-[50%] grid grid-cols-1 items-start gap-4">
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="enableDhcp"
                            name="enableDhcp"
                            checked={editSubnet.values.enableDhcp}
                            disabled={true}
                            onChange={(event) =>
                                editSubnet.setFieldValue("enableDhcp", event.target.checked)
                            }
                        />
                        <label htmlFor="disableGateway" className="text-sm text-gray-700 dark:text-gray-300">
                            Enable DHCP
                        </label>
                    </div>


                    <div>
                        <TextArea
                            type={"text"}
                            label="Allocation Pools"
                            name={"allocationPools"}
                            onBlur={editSubnet.handleBlur}
                            disabled={true}
                            onChange={(event) => {
                              const value = event.target.value;
                              const poolsArray = parseAllocationPools(value);
                              editSubnet.setFieldValue("allocationPools", poolsArray);
                            }}
                            placeholder={"IP address allocation pools. Each entry is: start_ip_address,end_ip_address (e.g., 192.168.1.100,192.168.1.120) and one entry per line."}
                            isLabelIcon={true}
                            iconTitle="IP address allocation pools. Each entry is: start_ip_address,end_ip_address (e.g., 192.168.1.100,192.168.1.120) and one entry per line."
                            value={formatAllocationPools(editSubnet.values.allocationPools)}
                            onError={
                                editSubnet.errors.allocationPools && editSubnet.touched.allocationPools
                                ? editSubnet.errors.allocationPools
                                : null
                            }
                        />
                    </div>


                    <div>
                        <TextArea
                            type={"text"}
                            label="DNS Name Servers"
                            name={"dnsServers"}
                            onBlur={editSubnet.handleBlur}
                            disabled={true}
                            onChange={(event) => {
                                const value = event.target.value;
                                const serversArray = value ? value.split("\n").filter((line) => line.trim() !== "") : [];
                                editSubnet.setFieldValue("dnsServers", serversArray);
                            }}
                            placeholder={"IP address list of DNS name servers for this subnet. One entry per line."}
                            isLabelIcon={true}
                            iconTitle="IP address list of DNS name servers for this subnet. One entry per line."
                            value={editSubnet.values.dnsServers.join("\n")} 
                            onError={
                                editSubnet.errors.dnsServers && editSubnet.touched.dnsServers
                                ? editSubnet.errors.dnsServers
                                : null
                            }
                        />
                    </div>




                </div>

                <div className="mt-6">
                    <button
                        type="submit"
                        className="p-4 rounded-md bg-primaryBlue text-white text-sm"
                    >
                        Update Subnet
                    </button>
                </div>
            </div>
          )}  
  

        </form>
      </div>
    </div>
  );
};

export default EditSubnet;